import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { FaExclamationTriangle } from 'react-icons/fa';
import { FiX } from 'react-icons/fi';
import dayjs from 'dayjs';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useClick,
  useDismiss,
  useInteractions,
} from '@floating-ui/react';
import { zIndexes } from '../../../../../styling/zIndexes';
import { mainColors } from '../../../../../styling/theme';

/** How far before expiry the overview warning button is shown. */
const DEROGATION_ALERT_LEAD_MONTHS = 1;

export function getDerogationDisplay(
  result: string | undefined,
  derogationDate: string | undefined,
  now = dayjs(),
): { date: string; showMessage: boolean; showAlert: boolean } | undefined {
  if (result !== 'live' || !derogationDate) return undefined;
  const expiry = dayjs(derogationDate).startOf('day');
  if (!expiry.isValid()) return undefined;
  const today = now.startOf('day');
  const upcoming = !expiry.isBefore(today);
  return {
    date: derogationDate,
    showMessage: upcoming,
    showAlert:
      upcoming &&
      !expiry.isAfter(today.add(DEROGATION_ALERT_LEAD_MONTHS, 'month')),
  };
}

export function formatDerogationMessage(derogationDate: string): string {
  return `Derogation Expiry Date: ${derogationDate}`;
}

function remainingLabel(expiryDate: string, now = dayjs()): string {
  const days = dayjs(expiryDate)
    .startOf('day')
    .diff(now.startOf('day'), 'day');
  if (days < 0) {
    const ago = Math.abs(days);
    return `Expired ${ago} day${ago === 1 ? '' : 's'} ago`;
  }
  if (days === 0) return 'Expires today';
  return `Expires in ${days} day${days === 1 ? '' : 's'}`;
}

export default function DerogationAlert({
  expiryDate,
}: {
  expiryDate: string;
}): React.ReactElement {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'right-start',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(8),
      flip({
        fallbackPlacements: ['left-start', 'right-end', 'left-end'],
      }),
      shift({ padding: 8 }),
    ],
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  return (
    <>
      <button
        ref={refs.setReference}
        {...getReferenceProps({
          onClick: (e) => {
            e.stopPropagation();
          },
        })}
        type="button"
        className={`group relative flex aspect-square h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
          isOpen
            ? 'border-fail bg-fail text-white shadow-lg ring-2 ring-fail ring-offset-1'
            : 'border-gray-300 bg-white text-fail shadow-sm hover:border-fail hover:bg-fail hover:text-white hover:shadow-md'
        }`}
        aria-label={`Derogation expiry date ${expiryDate}`}
      >
        <FaExclamationTriangle className="h-4 w-4 transition-transform duration-200 group-hover:scale-110" />
        <span className="absolute -right-1 -top-1 z-10 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white shadow-lg ring-2 ring-white">
          !
        </span>
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={refs.setFloating}
            style={{ ...floatingStyles, zIndex: zIndexes.notesPopover }}
            {...getFloatingProps()}
            className="w-[280px] overflow-hidden rounded-lg border border-fail bg-slate-50 shadow-xl"
          >
            <div
              className="flex shrink-0 items-center justify-between border-b px-4 py-2.5"
              style={{
                backgroundColor: mainColors.Fail,
                borderColor: mainColors.Fail,
              }}
            >
              <div className="flex items-center gap-1.5">
                <div className="flex h-6 w-6 items-center justify-center rounded bg-white/15">
                  <FaExclamationTriangle className="h-3.5 w-3.5 text-white" />
                </div>
                <h2 className="text-sm font-semibold text-white">
                  Derogation
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="flex h-6 w-6 items-center justify-center rounded text-white/80 transition-colors hover:bg-white/15 hover:text-white focus:outline-none focus:ring-1 focus:ring-white/60"
                aria-label="Close derogation alert"
              >
                <FiX className="h-4 w-4" />
              </button>
            </div>
            <div className="px-4 py-3">
              <p className="text-xs font-medium text-gray-900">
                Expiry date: {expiryDate}
              </p>
              <p className="mt-0.5 text-xs text-fail">
                {remainingLabel(expiryDate)}
              </p>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
